import { useContext, useState } from "react";

import { CityContext } from "@/contexts/city-context-provider";
import { formatApiError } from "@/services/web/api-error";
import { FeeConfigFormValues, feeConfigFormToInput } from "../fee-config.const";
import { FeeConfig, FeeConfigStatusEnum } from "../fee-config.type";
import { toastError, toastSuccess } from "../parking-payment.const";
import { ParkingRateStatusEnum } from "../payment-zone-area.type";
import {
  useCreateFeeConfig,
  useCreateFeeConfigVersion,
  useDeleteFeeConfig,
  useDisableFeeConfig,
  useGetFeeConfigs,
  usePropagateFeeConfig,
  usePublishFeeConfig,
  useUpdateFeeConfig,
} from "./use-fee-config-query";
import { useGetParkingRates } from "./use-parking-rate-query";

export type FeeConfigEditorState =
  | { mode: "create" }
  | { mode: "edit"; config: FeeConfig }
  | { mode: "view"; config: FeeConfig };

export const useFeeConfigs = () => {
  const { currentCity } = useContext(CityContext);
  const cityId = currentCity?.id ?? "";

  const { feeConfigs, isLoading: feeConfigsLoading } = useGetFeeConfigs(cityId);
  const { parkingRates } = useGetParkingRates(cityId);

  const { createFeeConfig } = useCreateFeeConfig(cityId);
  const { updateFeeConfig } = useUpdateFeeConfig(cityId);
  const { createFeeConfigVersion } = useCreateFeeConfigVersion(cityId);
  const { publishFeeConfig, isLoading: isPublishing } = usePublishFeeConfig(cityId);
  const { disableFeeConfig, isLoading: isDisabling } = useDisableFeeConfig(cityId);
  const { propagateFeeConfig, isLoading: isPropagating } = usePropagateFeeConfig(cityId);
  const { deleteFeeConfig, isLoading: isDeleting } = useDeleteFeeConfig(cityId);

  const [editorState, setEditorState] = useState<FeeConfigEditorState | null>(null);
  const [configToPublish, setConfigToPublish] = useState<FeeConfig | null>(null);
  const [configToDisable, setConfigToDisable] = useState<FeeConfig | null>(null);
  const [configToDelete, setConfigToDelete] = useState<FeeConfig | null>(null);
  const [propagateTarget, setPropagateTarget] = useState<FeeConfig | null>(null);

  const countRatesOnOlderVersions = (config: FeeConfig): number => {
    const siblingIds = new Set(
      feeConfigs.filter((c) => c.logicalFeeId === config.logicalFeeId && c.id !== config.id).map((c) => c.id),
    );
    return parkingRates.filter(
      (rate) => rate.status === ParkingRateStatusEnum.ENABLED && !!rate.feeConfigId && siblingIds.has(rate.feeConfigId),
    ).length;
  };

  const findEnabledSibling = (config: FeeConfig): FeeConfig | undefined =>
    feeConfigs.find(
      (c) => c.logicalFeeId === config.logicalFeeId && c.id !== config.id && c.status === FeeConfigStatusEnum.ENABLED,
    );

  const submitEditor = async (values: FeeConfigFormValues) => {
    if (!editorState || editorState.mode === "view") return;
    const input = feeConfigFormToInput(values);
    try {
      if (editorState.mode === "create") {
        await createFeeConfig(input);
        toastSuccess("La commission a été créée en brouillon.");
      } else {
        await updateFeeConfig({ id: editorState.config.id, input });
        toastSuccess("Le brouillon a été enregistré.");
      }
      setEditorState(null);
    } catch (error) {
      toastError(formatApiError(error, "Une erreur est survenue lors de l'enregistrement de la commission."));
    }
  };

  const handleCreateVersion = async (config: FeeConfig) => {
    try {
      const draft = await createFeeConfigVersion(config.id);
      setEditorState({ mode: "edit", config: draft });
    } catch (error) {
      toastError(formatApiError(error, "Une erreur est survenue lors de la création du brouillon."));
    }
  };

  const confirmPublish = async () => {
    if (!configToPublish) return;
    try {
      const published = await publishFeeConfig(configToPublish.id);
      toastSuccess(`La version v${published.version} de « ${published.name} » est maintenant active.`);
      if (countRatesOnOlderVersions(published) > 0) {
        setPropagateTarget(published);
      }
    } catch (error) {
      toastError(formatApiError(error, "Une erreur est survenue lors de la publication."));
    } finally {
      setConfigToPublish(null);
    }
  };

  const confirmPropagate = async () => {
    if (!propagateTarget) return;
    try {
      const drafts = await propagateFeeConfig(propagateTarget.id);
      toastSuccess(
        drafts.length > 0
          ? `${drafts.length} brouillon(s) de tarification créé(s). Publiez-les depuis le panneau Tarifications.`
          : "Toutes les tarifications ciblent déjà cette version.",
      );
    } catch (error) {
      toastError(formatApiError(error, "Une erreur est survenue lors de la propagation."));
    } finally {
      setPropagateTarget(null);
    }
  };

  const confirmDisable = async () => {
    if (!configToDisable) return;
    try {
      await disableFeeConfig(configToDisable.id);
      toastSuccess("La commission a été désactivée.");
    } catch (error) {
      toastError(formatApiError(error, "Une erreur est survenue lors de la désactivation."));
    } finally {
      setConfigToDisable(null);
    }
  };

  const confirmDelete = async () => {
    if (!configToDelete) return;
    try {
      await deleteFeeConfig(configToDelete.id);
      toastSuccess("Le brouillon a été supprimé.");
    } catch (error) {
      toastError(formatApiError(error, "Une erreur est survenue lors de la suppression."));
    } finally {
      setConfigToDelete(null);
    }
  };

  return {
    cityId,
    feeConfigs,
    feeConfigsLoading,
    editorState,
    setEditorState,
    submitEditor,
    handleCreateVersion,
    configToPublish,
    setConfigToPublish,
    confirmPublish,
    isPublishing,
    propagateTarget,
    setPropagateTarget,
    confirmPropagate,
    isPropagating,
    configToDisable,
    setConfigToDisable,
    confirmDisable,
    isDisabling,
    configToDelete,
    setConfigToDelete,
    confirmDelete,
    isDeleting,
    countRatesOnOlderVersions,
    findEnabledSibling,
  };
};
